import { useEffect, useRef, useState } from "react";
import { AuthImage } from "@/components/admin/AuthImage";
import { Lock, QrCode } from "lucide-react";

const CARD_W = 210;
const CARD_H = 130;

/**
 * Simple visual editor surface. Deliberately NOT a design suite: move,
 * resize, select. Print guides and the locked Bing postal area are
 * always visible so nothing can be placed where Bing prints.
 */
export function PostcardCanvas({
  side, versionId, elements, selected, onSelect, onChange, spec, scale = 4,
  locked = false, artworkKey = 0, artScope = "panel",
}) {
  const ref = useRef(null);
  const drag = useRef(null);
  const [, force] = useState(0);
  const box = side === "front" ? (spec?.front_box_mm || [2, 2, 206, 126]) : (spec?.back_box_mm || [0, 2, 116, 126]);
  const postalX = spec?.postal_zone_x_mm ?? 116;
  const quiet = spec?.qr_quiet_zone_mm ?? 2;

  /* A dynamic QR can never be dragged or resized into an invalid place:
     its footprint INCLUDES the quiet zone, and on the reverse the Bing
     postal strip is a hard wall. Invalid states are clamped here so
     they can never be saved. */
  const limits = (el) => {
    const pad = el.kind === "qr" ? quiet : 0;
    const right = side === "back" ? Math.min(box[0] + box[2], postalX) : box[0] + box[2];
    return { minX: box[0] + pad, minY: box[1] + pad,
             maxX: right - pad, maxY: box[1] + box[3] - pad };
  };
  const clampPos = (el, x, y) => {
    const L = limits(el);
    return {
      x_mm: Math.round(Math.min(Math.max(x, L.minX), Math.max(L.minX, L.maxX - el.w_mm)) * 10) / 10,
      y_mm: Math.round(Math.min(Math.max(y, L.minY), Math.max(L.minY, L.maxY - el.h_mm)) * 10) / 10,
    };
  };
  const clampSize = (el, w, h) => {
    const L = limits(el);
    let nw = Math.min(w, L.maxX - el.x_mm);
    let nh = Math.min(h, L.maxY - el.y_mm);
    if (el.kind === "qr") {
      const s = Math.max(spec?.min_qr_mm ?? 25, Math.min(nw, nh));
      nw = s; nh = s;
    } else { nw = Math.max(6, nw); nh = Math.max(4, nh); }
    return { w_mm: Math.round(nw * 10) / 10, h_mm: Math.round(nh * 10) / 10 };
  };

  useEffect(() => {
    const move = (e) => {
      const d = drag.current;
      if (!d) return;
      const dx = (e.clientX - d.px) / scale;
      const dy = (e.clientY - d.py) / scale;
      const el = d.el;
      let next;
      if (d.mode === "move") {
        next = { ...el, ...clampPos(el, d.x + dx, d.y + dy) };
      } else {
        next = { ...el, ...clampSize(el, d.w + dx, d.h + dy) };
      }
      d.latest = next;
      onChange(next, { transient: true });
      force((n) => n + 1);
    };
    const up = () => {
      const d = drag.current;
      drag.current = null;
      if (d?.latest) onChange(d.latest, { transient: false });
    };
    const key = (e) => {
      if (locked || !selected) return;
      const el = (elements || []).find((x) => x.id === selected);
      if (!el || el.locked) return;
      const step = e.shiftKey ? 5 : 1;
      const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0],
                  ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (!d) return;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      e.preventDefault();
      onChange({ ...el, ...clampPos(el, el.x_mm + d[0], el.y_mm + d[1]) },
               { transient: false });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("keydown", key);
    };
  }, [onChange, scale, selected, elements, locked, side]);

  const start = (e, el, mode) => {
    if (locked || el.locked) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect(el.id);
    drag.current = { px: e.clientX, py: e.clientY, el, mode, x: el.x_mm, y: el.y_mm, w: el.w_mm, h: el.h_mm };
  };

  return (
    <div className="overflow-x-auto" data-testid={`ps-canvas-scroll-${side}`}>
      <div
        ref={ref}
        data-testid={`ps-canvas-${side}`}
        onPointerDown={() => onSelect(null)}
        className="relative shrink-0 bg-white shadow-inner border border-slate-300"
        style={{ width: CARD_W * scale, height: CARD_H * scale, touchAction: "none" }}
      >
        {/* Artwork sits in the SAME box the renderer uses, so the canvas
            and the printed proof can never disagree: the front and a
            panel-shaped reverse use the safe box, a full-card reverse
            (210 x 130 design) spans the whole card. */}
        <div className="absolute pointer-events-none select-none"
          style={artScope === "full_card" && side === "back"
            ? { left: 0, top: 0, width: CARD_W * scale, height: CARD_H * scale }
            : { left: box[0] * scale, top: box[1] * scale,
                width: box[2] * scale, height: box[3] * scale }}>
          <AuthImage testId={`ps-canvas-art-${side}`} refreshKey={artworkKey} subtle
            emptyLabel={`No ${side} artwork uploaded yet`}
            path={versionId ? `/admin/postcard-studio/versions/${versionId}/artwork/${side}.png` : null}
            alt={`${side} artwork`}
            className="w-full h-full object-contain" />
        </div>
        {side === "back" && (
          <div className="absolute top-0 bottom-0 bg-white pointer-events-none"
            data-testid="ps-postal-mask"
            style={{ left: postalX * scale, width: (CARD_W - postalX) * scale }} />
        )}

        {/* print guides */}
        <div className="absolute border-2 border-dashed border-cyan-500/60 pointer-events-none"
          data-testid={`ps-guide-safe-${side}`}
          style={{ left: box[0] * scale, top: box[1] * scale, width: box[2] * scale, height: box[3] * scale }} />

        {side === "back" && (
          <div className="absolute top-0 bottom-0 pointer-events-none flex items-center justify-center text-center"
            data-testid="ps-postal-zone-locked"
            style={{
              left: postalX * scale, width: (CARD_W - postalX) * scale,
              backgroundImage: "repeating-linear-gradient(45deg, rgba(190,18,60,0.14) 0 8px, rgba(190,18,60,0.03) 8px 16px)",
              borderLeft: "2px solid rgba(190,18,60,0.7)",
            }}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-rose-800 px-2 leading-tight">
              <Lock className="w-3 h-3 inline mb-0.5 mr-0.5" />
              BING POSTAL AREA — LOCKED
            </span>
          </div>
        )}

        {(elements || []).map((el) => {
          const sel = selected === el.id;
          const isQr = el.kind === "qr";
          return (
            <div key={el.id} data-testid={`ps-el-${el.id}`}
              onPointerDown={(e) => start(e, el, "move")}
              className={`absolute ${sel ? "outline outline-2 outline-cyan-600" : "outline outline-1 outline-slate-400/50"} ${el.locked || locked ? "cursor-not-allowed" : "cursor-move"}`}
              style={{
                left: el.x_mm * scale, top: el.y_mm * scale,
                width: el.w_mm * scale, height: el.h_mm * scale,
                background: isQr ? "#fff" : "rgba(255,255,255,0.55)",
              }}>
              {isQr ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-800"
                  data-testid={`ps-qr-placeholder-${side}`}>
                  <QrCode className="w-1/2 h-1/2" />
                  <span className="text-[9px] font-bold">Dynamic QR</span>
                  <span className="text-[9px] font-bold">{el.w_mm} × {el.h_mm} mm</span>
                </div>
              ) : (
                <div className="w-full h-full overflow-hidden px-0.5"
                  style={{
                    color: el.color, fontWeight: el.bold ? 700 : 400,
                    textAlign: el.align, fontSize: (el.font_size_pt || 12) * scale * 0.352,
                    lineHeight: el.line_spacing || 1.14, whiteSpace: "pre-wrap",
                    fontFamily: "Helvetica, Arial, sans-serif",
                  }}>
                  {el.text}
                </div>
              )}
              {isQr && (
                <div className="absolute pointer-events-none border border-dashed border-slate-400"
                  data-testid={`ps-qr-quiet-${el.id}`}
                  style={{ left: -quiet * scale, top: -quiet * scale,
                           right: -quiet * scale, bottom: -quiet * scale,
                           background: "rgba(255,255,255,0.75)", zIndex: -1 }} />
              )}
              {sel && !el.locked && !locked && (
                <div data-testid={`ps-el-resize-${el.id}`}
                  onPointerDown={(e) => start(e, el, "resize")}
                  className="absolute -right-1.5 -bottom-1.5 w-3.5 h-3.5 rounded-sm bg-cyan-600 border border-white cursor-se-resize" />
              )}
              {el.locked && (
                <Lock className="absolute -top-2 -left-2 w-3.5 h-3.5 text-slate-700 bg-white rounded-full p-[1px]" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
